import {
  type IBridgeDevice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type IAttachment,
  Firestore,
  type WithRef,
} from '@principle-theorem/shared';
import * as JSZip from 'jszip';
import { BridgeDevice } from './bridge-device';
import {
  type IBridgeConfigConflictReport,
  checkBridgeConfigConflicts,
} from './bridge-device-config-conflicts';
import {
  getBridgeDeviceDiagnosticStatuses,
  type IBridgeDeviceDiagnosticStatus,
} from './bridge-device-diagnostic-status';

export interface IBridgeDiagnosticReport {
  generatedAt: string;
  practiceName: string;
  practiceRef: string;
  devices: IBridgeDiagnosticReportDevice[];
  configConflicts: IBridgeConfigConflictReport;
}

export interface IBridgeDiagnosticReportDevice {
  deviceName: string;
  deviceId: string;
  status: string;
  logFreshness: string;
  logCount: number;
  latestLogDate?: string;
}

export interface IBridgeDiagnosticReportResult {
  zip: JSZip;
  report: IBridgeDiagnosticReport;
  totalLogFiles: number;
  rootFolder: string;
}

/**
 * Builds a diagnostic report zip containing:
 * - diagnostic-report.json: summary of all devices, statuses, and config conflicts
 * - <device-name>/<log-file>: all log files from each device
 *
 * Returns the JSZip instance for callers to output as needed
 * (saveAs in browser, writeFileSync in Node).
 */
export async function buildBridgeDiagnosticReport(
  practiceName: string,
  practiceRefPath: string,
  devices: WithRef<IBridgeDevice>[]
): Promise<IBridgeDiagnosticReportResult> {
  const zip = new JSZip();
  const dateStr = new Date().toISOString().slice(0, 10);
  const rootFolder = `${sanitiseFilename(practiceName)}-diagnostics-${dateStr}`;

  const diagnosticStatuses = await getBridgeDeviceDiagnosticStatuses(devices);
  const configConflicts = checkBridgeConfigConflicts(devices);

  const report: IBridgeDiagnosticReport = {
    generatedAt: new Date().toISOString(),
    practiceName,
    practiceRef: practiceRefPath,
    devices: mapDevicesToReport(diagnosticStatuses),
    configConflicts,
  };

  zip.file(
    `${rootFolder}/diagnostic-report.json`,
    JSON.stringify(report, undefined, 2)
  );

  const totalLogFiles = await addLogFilesToZip(zip, devices, rootFolder);

  return { zip, report, totalLogFiles, rootFolder };
}

function mapDevicesToReport(
  statuses: IBridgeDeviceDiagnosticStatus[]
): IBridgeDiagnosticReportDevice[] {
  return statuses.map((d) => ({
    deviceName: d.deviceName,
    deviceId: d.deviceId,
    status: d.status,
    logFreshness: d.logFreshness,
    logCount: d.logCount,
    latestLogDate: d.latestLogDate?.toISOString() ?? undefined,
  }));
}

async function addLogFilesToZip(
  zip: JSZip,
  devices: WithRef<IBridgeDevice>[],
  rootFolder: string
): Promise<number> {
  let totalFiles = 0;
  let skippedFiles = 0;

  for (const device of devices) {
    if (device.deleted) {
      continue;
    }

    const files = await Firestore.getDocs(BridgeDevice.fileCol(device));
    const logFiles = files.filter(
      (file: WithRef<IAttachment>) => file.type === 'log'
    );

    if (logFiles.length === 0) {
      continue;
    }

    const deviceFolder = sanitiseFilename(device.name);
    for (const logFile of logFiles) {
      const fileName = logFile.name ?? `${logFile.ref.id}.log`;
      try {
        const response = await fetch(logFile.link);
        if (response.ok) {
          const content = await response.arrayBuffer();
          zip.file(`${rootFolder}/${deviceFolder}/${fileName}`, content);
          totalFiles++;
        } else {
          skippedFiles++;
        }
      } catch {
        skippedFiles++;
      }
    }
  }

  if (skippedFiles > 0) {
    zip.file(
      `${rootFolder}/skipped-files.txt`,
      `${skippedFiles} log file(s) could not be downloaded. They may require authentication or have expired URLs.`
    );
  }

  return totalFiles;
}

function sanitiseFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
}
